import { PALETTE } from "../core/Theme";
import type { Frame } from "./Frame";

export function renderVolume(f: Frame) {
  const { ctx, L } = f;
  let maxV = 0, minV = Infinity;
  for (let i = Math.max(0, f.start); i <= f.end; i++) { const v = f.candles[i]?.volume || 0; if (v > maxV) maxV = v; if (v < minV) minV = v; }
  if (maxV <= 0 || maxV === minV) return; // constant volume (e.g. synthetic 1s tick candles = 1 tick each) carries no information
  const volTop = L.priceBottom - L.priceH * 0.18;
  for (let i = Math.max(0, f.start); i <= f.end; i++) {
    const c = f.candles[i];
    if (!c) continue;
    const bh = ((c.volume || 0) / maxV) * (L.priceBottom - volTop);
    ctx.fillStyle = c.close >= c.open ? "rgba(0,192,118,0.30)" : "rgba(255,59,48,0.30)";
    ctx.fillRect(f.x(i) - f.bodyW / 2, L.priceBottom - bh, f.bodyW, bh);
  }
}

export function renderSeries(f: Frame) {
  const { ctx, L, opts } = f;
  const cs = f.candles;
  const type = opts.chartType;
  const s0 = Math.max(0, f.start);
  if (type === "line" || type === "area") {
    ctx.beginPath();
    let started = false;
    for (let i = s0; i <= f.end; i++) {
      const c = cs[i]; if (!c) continue;
      const x = f.x(i), y = f.y(c.close);
      if (!started) { ctx.moveTo(x, y); started = true; } else ctx.lineTo(x, y);
    }
    ctx.strokeStyle = PALETTE.amber; ctx.lineWidth = 1.6; ctx.stroke();
    if (type === "area") {
      ctx.lineTo(f.x(f.end), L.priceBottom);
      ctx.lineTo(f.x(s0), L.priceBottom);
      ctx.closePath();
      const grad = ctx.createLinearGradient(0, L.plotTop, 0, L.priceBottom);
      grad.addColorStop(0, "rgba(245,166,35,0.28)");
      grad.addColorStop(1, "rgba(245,166,35,0.01)");
      ctx.fillStyle = grad; ctx.fill();
    }
    return;
  }
  const MIN_BODY_H = 2; // visual-only: zero-range (O=H=L=C) candles still get an identifiable body; prices are never altered
  for (let i = s0; i <= f.end; i++) {
    const c = cs[i]; if (!c) continue;
    const x = f.x(i);
    const prev = cs[i - 1];
    const up = c.close !== c.open ? c.close > c.open : prev ? c.close >= prev.close : true; // neutral candle → colour vs previous close
    const stroke = up ? PALETTE.bullStroke : PALETTE.bearStroke;
    const fill = up ? PALETTE.bullFill : PALETTE.bearFill;
    const yO = f.y(c.open), yC = f.y(c.close), yH = f.y(c.high), yL = f.y(c.low);
    if (type === "ohlc") {
      ctx.strokeStyle = stroke; ctx.lineWidth = 1.4;
      ctx.beginPath(); ctx.moveTo(x, yH); ctx.lineTo(x, yL); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(x - f.bodyW / 2, yO); ctx.lineTo(x, yO); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(x, yC); ctx.lineTo(x + f.bodyW / 2, yC); ctx.stroke();
      continue;
    }
    ctx.strokeStyle = stroke; ctx.lineWidth = 1;
    if (c.high > Math.max(c.open, c.close) || c.low < Math.min(c.open, c.close)) { ctx.beginPath(); ctx.moveTo(x, yH); ctx.lineTo(x, yL); ctx.stroke(); }
    const rawH = Math.abs(yC - yO);
    const bh = Math.max(MIN_BODY_H, rawH);
    const top = rawH >= MIN_BODY_H ? Math.min(yO, yC) : (yO + yC) / 2 - bh / 2; // centre the minimum glyph on the exact price
    const bx = Math.round(x - f.bodyW / 2) + 0.5, bw = Math.max(1, Math.round(f.bodyW));
    if (type === "hollow" && up) { ctx.strokeRect(bx, top, bw, bh); }
    else { ctx.fillStyle = fill; ctx.fillRect(bx, top, bw, bh); if (bw >= 3) ctx.strokeRect(bx, top, bw, bh); }
  }
}

export function renderLastPrice(f: Frame) {
  const { ctx, L, opts } = f;
  const last = f.candles[f.candles.length - 1];
  if (!last) return;
  const lastY = f.y(last.close);
  if (lastY <= L.plotTop || lastY >= L.priceBottom) return;
  const up = last.close >= last.open;
  ctx.strokeStyle = up ? PALETTE.bullStroke : PALETTE.bearStroke;
  ctx.setLineDash([4, 4]); ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(L.plotLeft, lastY); ctx.lineTo(L.plotRight, lastY); ctx.stroke();
  ctx.setLineDash([]);
  ctx.fillStyle = up ? PALETTE.bullFill : PALETTE.bearFill;
  ctx.fillRect(L.plotRight, lastY - 9, L.rightAxis, 18);
  ctx.fillStyle = "#fff";
  ctx.font = `11px ${PALETTE.mono}`;
  ctx.textAlign = "left"; ctx.textBaseline = "middle";
  ctx.fillText(last.close.toFixed(opts.decimals), L.plotRight + 5, lastY);
}
